import type { Editor, Component } from 'grapesjs';
import { IClipboardData, IIdMapping, IStyleData } from './types';
import {
  applyImportantFlags,
  buildIdMapping,
  collectComponentStyles,
  createSetRuleOptions,
  deserializeClipboardData,
  groupStylesByMedia,
  isValidClipboardData,
  isValidStyleData,
  processSelector,
} from './utils';

export const STORAGE_KEY = 'grapesjs_clipboard';

export class CrossTabClipboard {
  private editor: Editor;

  public constructor(editor: Editor) {
    this.editor = editor;
  }

  public copy(components: Component[]): void {
    try {
      const serializedComponents = components.map(component => component.toJSON());
      const cssComposer = this.editor.Css;
      const allStyles: IStyleData[] = [];
      const processedRuleSignatures = new Set<string>();

      // Собираем стили для всех компонентов
      const allRules = Array.from(cssComposer.getAll());
      components.forEach(component => {
        const componentStyles = collectComponentStyles(component, allRules, processedRuleSignatures);
        allStyles.push(...componentStyles);
      });

      const clipboardData: IClipboardData = {
        components: serializedComponents,
        styles: allStyles,
        timestamp: Date.now(),
      };

      const serializedData = JSON.stringify(clipboardData);
      localStorage.setItem(STORAGE_KEY, serializedData);
    } catch (error) {
      console.info('[CrossTabClipboard] Error copying components:', error);
    }
  }

  public paste(): Component[] | null {
    try {
      const storedData = localStorage.getItem(STORAGE_KEY);
      if (!storedData) {
        return null;
      }

      const clipboardData = deserializeClipboardData(storedData);
      if (!clipboardData || !isValidClipboardData(clipboardData)) {
        console.info('[CrossTabClipboard] Invalid clipboard data');

        return null;
      }

      const wrapper = this.editor.getWrapper();
      if (!wrapper) {
        console.info('[CrossTabClipboard] No wrapper found');

        return null;
      }

      // Добавляем компоненты и позволяем GrapesJS сгенерировать новые ID
      const addedComponents: Component[] = [];
      clipboardData.components.forEach((componentData: Record<string, unknown>) => {
        const added = wrapper.append(componentData)[0];
        // Клонируем для получения свежих ID
        const cloned = added.clone();
        added.remove();
        const finalComponent = wrapper.append(cloned)[0];
        addedComponents.push(finalComponent);
      });

      // Создаем маппинг старых ID на новые для обновления CSS селекторов
      const idMapping: IIdMapping = {};
      clipboardData.components.forEach((componentData: Record<string, unknown>, index: number) => {
        buildIdMapping(componentData, addedComponents[index], idMapping);
      });

      // Применяем собранные стили с учетом состояний и медиазапросов
      if (clipboardData.styles && clipboardData.styles.length > 0) {
        this.applyStyles(clipboardData.styles, idMapping);
      }

      // Выбираем вставленные компоненты
      if (addedComponents.length > 0) {
        this.editor.select(addedComponents);
      }

      return addedComponents;
    } catch (error) {
      console.info('[CrossTabClipboard] Error pasting:', error);

      return null;
    }
  }

  private applyStyles(styles: Record<string, unknown>[], idMapping: IIdMapping): void {
    const cssComposer = this.editor.Css;
    const stylesByMedia = groupStylesByMedia(styles);

    Object.entries(stylesByMedia).forEach(([, mediaStyles]) => {
      mediaStyles.forEach((styleData: Record<string, unknown>) => {
        try {
          if (!isValidStyleData(styleData)) {
            return;
          }

          const selectorString = processSelector(styleData.selectors, idMapping);
          if (!selectorString) {
            return;
          }

          const options = createSetRuleOptions(styleData);

          // Применяем правило с селектором
          const rule = cssComposer.setRule(selectorString, styleData.style, options);

          // Применяем флаги !important если есть
          if (styleData.important) {
            applyImportantFlags(rule, styleData.important);
          }
        } catch (err) {
          console.info('[CrossTabClipboard] Error applying style:', err, styleData);
        }
      });
    });

    this.editor.refresh();
  }

  public clear(): void {
    localStorage.removeItem(STORAGE_KEY);
  }

  public hasData(): boolean {
    return localStorage.getItem(STORAGE_KEY) !== null;
  }
}
